import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { isClosedOrDeletedStatus, isNullifiedStatus } from '@utils/claimUtil'
import { createResourceNotFoundFetchError, isResourceNotFoundError } from '@utils/errorUtil'
import { resetClaimRecordState } from '@utils/resetClaimRecordState'

type UseFetchClaimRecordReturn = {
  isLoading?: boolean
  isError?: boolean

  /**
   * Fetch latest claim record
   * @throws fetch error with status 404 if claim record status is logically closed
   */
  fetchClaimRecord: (id: string) => Promise<ClaimRecord> | never

  fetchClaimRecordRaw: (id: string, preferCacheValue?: boolean) => Promise<ClaimRecord>
}

export const useFetchClaimRecord = (): UseFetchClaimRecordReturn => {
  const dispatch = useDispatch()
  const [triggerFetch, { isError, isFetching, isLoading }] = useLazyGetClaimRecordQuery()

  const fetchClaimRecord = useCallback(
    async (id: string): Promise<ClaimRecord> | never => {
      try {
        const claimRecord = await triggerFetch(id).unwrap()

        const isLogicallyClosed = isNullifiedStatus(claimRecord?.status) || isClosedOrDeletedStatus(claimRecord?.status)
        if (isLogicallyClosed) {
          throw createResourceNotFoundFetchError()
        }

        return claimRecord
      } catch (error) {
        if (isResourceNotFoundError(error)) {
          resetClaimRecordState(dispatch)
        }
        throw error
      }
    },
    [dispatch, triggerFetch]
  )
  const fetchClaimRecordRaw = useCallback(
    async (id: string, preferCacheValue?: boolean): Promise<ClaimRecord> => {
      return triggerFetch(id, preferCacheValue).unwrap()
    },
    [triggerFetch]
  )

  return {
    isError,
    isLoading: isFetching || isLoading,
    fetchClaimRecord,
    fetchClaimRecordRaw,
  }
}
